// apps/web/src/widgets/recipe-state-provider.tsx
//
// Phase 6 — D-09 in-memory recipe-state provider.
// Exposes an immutable Map of widgetInstanceId → WidgetState plus a setter that
// produces a new Map on every write (so React + Phase 9 selectors observe a
// stable identity per write).
//
// Phase 11.3 Plan 04 WIRES the strategy: on mount the provider calls
// strategy.load(recipeId) → setStates (seeding the Map from the server), and a
// debounced (~300ms) useEffect calls strategy.save(recipeId, states) on change.
// A hasLoadedRef gate suppresses the load-driven setStates from immediately
// re-firing save (RESEARCH Pattern 3 / Pitfall 2 — no load→save feedback loop),
// and the debounce timer is cleared on unmount (Pitfall 5 — no stray PUT after
// unmount). The default `strategy = memoryStrategy` keeps previews + the render
// pane + tests no-op (load → empty Map, save → noop), so only the authed view
// routes (which pass the http strategy) actually persist.
//
// useSyncExternalStore migration is RESOLVED-deferred per RESEARCH §Open
// Questions [1] — the Map-snapshot approach is sufficient because every write
// produces a fresh Map identity; subscribers re-render via Context.
import * as React from "react";
import type { WidgetInstanceId, WidgetState } from "./widget-renderer-props.js";

export interface RecipeStateContextValue {
  states: ReadonlyMap<WidgetInstanceId, WidgetState>;
  setWidgetState(widgetId: WidgetInstanceId, next: WidgetState): void;
}

export interface PersistenceStrategy {
  load(recipeId: string): Promise<ReadonlyMap<WidgetInstanceId, WidgetState>>;
  save(recipeId: string, states: ReadonlyMap<WidgetInstanceId, WidgetState>): Promise<void>;
  subscribe(
    recipeId: string,
    cb: (states: ReadonlyMap<WidgetInstanceId, WidgetState>) => void,
  ): () => void;
}

export interface RecipeStateProviderProps {
  recipeId: string;
  strategy?: PersistenceStrategy;
  /**
   * Phase 6 test-only escape hatch — seed the in-memory Map. Phase 11 wires
   * strategy.load() instead.
   */
  initialStates?: ReadonlyMap<WidgetInstanceId, WidgetState>;
  children: React.ReactNode;
}

export const RecipeStateContext = React.createContext<RecipeStateContextValue | null>(null);

// Phase 6 default — no persistence. The interface IS stable for Phase 11.
export const memoryStrategy: PersistenceStrategy = {
  load: async () => new Map(),
  save: async () => {},
  subscribe: () => () => {},
};

export function RecipeStateProvider({
  recipeId,
  strategy = memoryStrategy,
  initialStates,
  children,
}: RecipeStateProviderProps): React.ReactElement {
  const [states, setStates] = React.useState<ReadonlyMap<WidgetInstanceId, WidgetState>>(
    () => initialStates ?? new Map(),
  );

  // hasLoaded gate — the mount-load below calls setStates with the server blob,
  // which would otherwise trip the debounced-save effect into PUTting back
  // exactly what we just loaded (RESEARCH Pattern 3 / Pitfall 2). The save
  // effect early-returns until this flips true (after the first load resolves),
  // so a user change before the load never races a half-loaded blob to the
  // server. A re-keyed remount (recipeId/strategy change) resets it.
  const hasLoadedRef = React.useRef(false);
  // skipNextSave — the setStates(loaded) inside the mount-load re-runs the save
  // effect by identity. That single run is the hydration echo and MUST NOT PUT.
  // We arm this when the load resolves and the save effect consumes it (clears +
  // returns) on exactly that run; every later user-driven change saves normally.
  const skipNextSaveRef = React.useRef(false);

  // Flush-on-rekey/unmount plumbing (review MD-02 — no silent data loss).
  // `pendingRef` is true while a user-driven edit is sitting in the 300ms
  // debounce window un-PUT. `latestStatesRef` always mirrors the most-recent
  // Map so the flush effect (which is keyed on recipeId/strategy, NOT states)
  // can PUT the freshest blob for the OUTGOING scope when the active recipe is
  // switched or the provider unmounts — instead of clearTimeout silently
  // dropping it. The debounce effect clears `pendingRef` once it PUTs.
  const pendingRef = React.useRef(false);
  const latestStatesRef = React.useRef(states);
  latestStatesRef.current = states;

  // Mount load — seed the Map from the strategy. memoryStrategy.load resolves an
  // empty Map (previews/tests stay no-op); the http strategy GETs the server
  // row. Guard against set-after-unmount with a `cancelled` flag.
  React.useEffect(() => {
    let cancelled = false;
    hasLoadedRef.current = false;
    // LW-02 — reset skipNextSave alongside hasLoaded so BOTH gates re-initialize
    // together on every re-key. Otherwise a `skipNextSaveRef = true` armed by the
    // previous scope's load (but not yet consumed before the re-key) would survive
    // and suppress exactly one legitimate save after the NEW load resolves.
    skipNextSaveRef.current = false;
    void strategy
      .load(recipeId)
      .then((loaded) => {
        if (!cancelled) {
          // Order matters: arm the skip BEFORE setStates so the save effect this
          // setStates schedules sees skipNextSaveRef === true and suppresses the
          // hydration echo. hasLoaded opens the gate for subsequent real changes.
          skipNextSaveRef.current = true;
          hasLoadedRef.current = true;
          setStates(loaded);
        }
      })
      .catch(() => {
        // A rejected load must NOT crash the render (unhandled rejection) — the
        // http strategy already fails closed to an empty Map, but this catch is
        // defense-in-depth for any strategy whose load rejects. Keep the current
        // (initial/empty) state and OPEN the save gate so subsequent user edits
        // still persist. No setStates here means no hydration echo, so the skip
        // flag stays false.
        if (!cancelled) {
          hasLoadedRef.current = true;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recipeId, strategy]);

  // Debounced save (~300ms) — fires only AFTER the first load (hasLoaded gate)
  // and skips the single hydration-echo run (skipNextSave), so the load does not
  // PUT what it just read. A held toggle re-arms the timer (only the final blob
  // is PUT); clearTimeout on unmount prevents a stray PUT after the component is
  // gone (Pitfall 5). memoryStrategy.save is a no-op, so this is inert on
  // previews/tests that don't pass a real strategy.
  React.useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    // A real user-driven edit is now in the debounce window: mark it pending so
    // the flush effect can rescue it on a re-key/unmount (MD-02). The timer
    // PUTs the blob and clears the pending flag; cleanup on a NORMAL states
    // change just cancels the timer (re-debounce) — the held-toggle cadence is
    // preserved because only the final blob is PUT.
    pendingRef.current = true;
    const id = setTimeout(() => {
      pendingRef.current = false;
      void strategy.save(recipeId, states);
    }, 300);
    return () => clearTimeout(id);
  }, [states, recipeId, strategy]);

  // Flush-on-rekey/unmount (MD-02). Keyed on recipeId/strategy ONLY (NOT states),
  // so this effect's CLEANUP runs exactly when the active recipe switches (the
  // strategy/recipeId re-key) or the provider unmounts — the two moments the
  // debounce-effect's clearTimeout would otherwise silently drop a sub-300ms
  // edit. The cleanup closure captures the OUTGOING recipeId + strategy; it PUTs
  // the freshest blob via latestStatesRef. It fires ONLY when pendingRef is true
  // (a genuine un-PUT edit), so a re-key with nothing pending stays a no-op and
  // does NOT echo the hydrated-but-unchanged state back to the server.
  React.useEffect(() => {
    return () => {
      if (pendingRef.current) {
        pendingRef.current = false;
        void strategy.save(recipeId, latestStatesRef.current);
      }
    };
  }, [recipeId, strategy]);

  const setWidgetState = React.useCallback(
    (widgetId: WidgetInstanceId, next: WidgetState): void => {
      setStates((prev) => {
        const m = new Map(prev);
        m.set(widgetId, next);
        return m;
      });
    },
    [],
  );

  const value = React.useMemo<RecipeStateContextValue>(
    () => ({ states, setWidgetState }),
    [states, setWidgetState],
  );

  return <RecipeStateContext.Provider value={value}>{children}</RecipeStateContext.Provider>;
}
